import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Element, ItemCollectibleVariant } from '@outdoria/types';
import { fetcher } from '../utils';
import {
  ADMIN_COLLECTIBLE_VARIANT_ALL,
  ADMIN_COLLECTIBLE_VARIANT_UPDATE,
  COLLECTIBLE_VARIANT,
  ITEM,
} from '../queryKeys';

type Data = {
  variant: Omit<
    ItemCollectibleVariant,
    'item_id' | 'item_collectible_variant_id'
  >;
  elements: Element['element_id'][];
};
export const useAdminUpdateCollectibleVariant = (
  item_collectible_variant_id: string,
) => {
  const queryClient = useQueryClient();

  const mutationFn = async ({ variant, elements }: Data): Promise<void> => {
    const response = await fetcher(
      `/v2/admin/items/collectibles/variant/${item_collectible_variant_id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ ...variant, elements }),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );

    if (response.status !== 204)
      throw new Error(
        (await response.json()).error ??
          'Could not update the collectible variant for some unforeseen reason!',
      );

    queryClient.invalidateQueries({ queryKey: ADMIN_COLLECTIBLE_VARIANT_ALL });
    queryClient.invalidateQueries({
      queryKey: COLLECTIBLE_VARIANT,
      exact: false,
    });
    queryClient.invalidateQueries({ queryKey: ITEM, exact: false });
  };

  const { mutate, error, status, isPending, isSuccess, isError } = useMutation({
    mutationKey: ADMIN_COLLECTIBLE_VARIANT_UPDATE,
    mutationFn,
  });

  return { mutate, error, status, isPending, isSuccess, isError };
};
